import { useState, type FunctionComponent } from "react";
import { BankOutlined, DownOutlined, GiftOutlined, LinkOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  DatePicker,
  Dropdown,
  Form,
  InputNumber,
  message,
  Modal,
  Space,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";

import { productClientActions } from "@fitness-app/app-store";
import { PayoffErrorCode, type PayoffGrantOfferResponse } from "@fitness-app/data-models/entities/Payoff";
import { type SubscriptionProductClient } from "@fitness-app/data-models/entities/ProductClient";
import { areInstallmentsSettled } from "@fitness-app/utils/src/products/areInstallmentsSettled";

import { PayoffOfferCampaignFields } from "~/modules/Products/components/PayoffOfferCampaign/PayoffOfferCampaignFields";
import {
  buildGrantOfferPayload,
  DEFAULT_BONUS_DAYS,
  DEFAULT_DISCOUNT_PCT,
  DEFAULT_FOLLOW_UP_DAYS,
  DEFAULT_OFFER_DAYS,
  followUpLabel,
  isOfferOpen,
  rewardLabel,
  type PayoffOfferCampaignModel,
} from "~/modules/Products/components/PayoffOfferCampaign/payoffOfferCampaign";
import { productApi, useAppDispatch, useAppSelector } from "~/store/initializeStore";

import { isLocallyPayoffEligible, isLocallySettleable } from "./payoffActionGates";
import { PayoffOfflineSettlementModal } from "./PayoffOfflineSettlementModal";

interface OwnProps {
  productClient: SubscriptionProductClient;
}

type OfferFormModel = PayoffOfferCampaignModel;

/**
 * Does the PRICE of this plan still offer the payoff on its own (TRE-1514)?
 *
 * `null` means "cannot tell" — the client's orders are not in the store yet, so
 * the price behind the plan is unknown. Callers treat that as "offer probably
 * there" and keep the link action, instead of hiding a working button.
 */
const usePriceLevelOffer = (productClient: SubscriptionProductClient): boolean | null => {
  const product = useAppSelector((store) => store.product.details);
  const clientOrders = useAppSelector((store) => store.productClient.clientOrders);

  const order =
    clientOrders.find((clientOrder) => clientOrder.id === productClient.lastOrder) ??
    clientOrders.find((clientOrder) => Boolean(clientOrder.priceId));

  if (!product || !order?.priceId) {
    return null;
  }

  const price = product.prices?.find((productPrice) => productPrice.id === order.priceId);

  if (!price) {
    return null;
  }

  const config = price.config?.payoffUpsell;

  if (!config?.enabled || !config.windowDays || !config.discountPct) {
    return false;
  }

  return dayjs(productClient.createdAt).add(config.windowDays, "day").valueOf() > Date.now();
};

const LINK_ERROR_MESSAGES: Record<string, string> = {
  [PayoffErrorCode.WindowExpired]: "Okno dopłaty tego planu już wygasło.",
  [PayoffErrorCode.AlreadyPaid]: "Ten plan jest już opłacony w całości.",
  [PayoffErrorCode.NotEligible]:
    "Plan nie kwalifikuje się do dopłaty (np. zbliżająca się rata — blokada ±48h albo wyłączony payoff).",
};

/**
 * Payoff actions on a client (TRE-1638 + TRE-1713).
 *
 * Deliberately ONE primary action at a time, because the two are mutually
 * exclusive in practice: without an offer the magic link can only fail
 * ("not eligible"), and with an offer in place granting another one is not what
 * the operator is after. So: no offer → grant one; offer (granted here or
 * coming from the price config) → mint the link, with editing and revoking as
 * secondary actions.
 */
export const PayoffOfferActions: FunctionComponent<OwnProps> = ({ productClient }) => {
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showSettleModal, setShowSettleModal] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formController] = Form.useForm<OfferFormModel>();
  // The campaign fields switch on each other (no email → no reminder), so they
  // need the live values, not just the submitted ones.
  const formValues = Form.useWatch([], formController) ?? {};
  const dispatch = useAppDispatch();

  const offer = productClient.payoffOffer;
  const isGrantActive = isOfferOpen(offer);
  const priceLevelOffer = usePriceLevelOffer(productClient);
  // Unknown price (orders not loaded) keeps the pre-TRE-1713 behaviour: show the
  // link and let the server answer.
  const hasOffer = isGrantActive || priceLevelOffer !== false;
  // The two halves of this menu answer different questions and are gated
  // separately (TRE-2016): an offer can be over while a transfer still has to be
  // booked. An offer already on the client keeps its own actions reachable —
  // editing and revoking it must not depend on her still qualifying for it.
  const canOffer = isLocallyPayoffEligible(productClient) || Boolean(offer);
  const canSettle = isLocallySettleable(productClient);
  // Nothing left to do once the plan is settled — the actions below would all
  // fail server-side, and the gold "offer until …" tag read as if the client
  // still owed something (TRE-1737). Same rule as everywhere else, including a
  // plan that simply billed out.
  const isPaidOff = areInstallmentsSettled(productClient.accessPeriod);

  if (!canOffer && !canSettle) {
    return null;
  }

  if (isPaidOff) {
    // The settled state itself is already on the line ("Dopłacone jednorazowo"),
    // so this only says WHICH offer was used — and nothing at all when the
    // client had none.
    return offer ? (
      <Space size={4} className="ml-4">
        <Tag>
          Wykorzystana oferta {rewardLabel(offer)}
          {offer.redeemedAt ? ` — ${dayjs(offer.redeemedAt).format("DD.MM.YYYY")}` : ""}
        </Tag>
      </Space>
    ) : null;
  }

  const refresh = () =>
    dispatch(productClientActions.fetchProductClient({ productId: productClient.productId, id: productClient.id }));

  const openOfferModal = () => {
    formController.setFieldsValue({
      rewardType: offer?.reward?.type ?? "discount",
      discountPct: offer?.discountPct || DEFAULT_DISCOUNT_PCT,
      bonusDays: offer?.reward?.type === "bonusTime" ? offer.reward.bonusDays : DEFAULT_BONUS_DAYS,
      expiresAt: dayjs(isGrantActive && offer ? offer.expiresAt : dayjs().add(DEFAULT_OFFER_DAYS, "day")),
      sendEmail: !isGrantActive,
      followUpEnabled: Boolean(offer?.followUp?.enabled),
      followUpAfterDays: offer?.followUp?.afterDays ?? DEFAULT_FOLLOW_UP_DAYS,
    });
    setShowOfferModal(true);
  };

  const onSubmit = async (data: OfferFormModel) => {
    setSaving(true);

    try {
      const { data: result } = await productApi.post<PayoffGrantOfferResponse>(
        "/payoff/grant-offer",
        buildGrantOfferPayload(data, [productClient.id]),
      );

      if (result.granted.length) {
        void message.success(data.sendEmail ? "Oferta przyznana, mail w drodze" : "Oferta przyznana");
        setShowOfferModal(false);
        void refresh();
      } else {
        void message.warning(result.skipped[0]?.reason ?? "Nie udało się przyznać oferty");
      }
    } catch {
      void message.error("Nie udało się przyznać oferty");
    } finally {
      setSaving(false);
    }
  };

  const openSettleModal = () => setShowSettleModal(true);

  const onRevoke = async () => {
    setSaving(true);

    try {
      await productApi.post("/payoff/revoke-offer", { productClientIds: [productClient.id] });
      void message.success("Oferta cofnięta");
      setShowRevokeConfirm(false);
      void refresh();
    } catch {
      void message.error("Nie udało się cofnąć oferty");
    } finally {
      setSaving(false);
    }
  };

  const generateLink = async () => {
    setIsGeneratingLink(true);
    setLinkError(null);
    setLink(null);
    setShowLinkModal(true);

    try {
      const { data } = await productApi.post<{ url: string; expiresAt: number }>("/payoff/admin-magic-link", {
        productClientId: productClient.id,
      });

      setLink(data.url);
    } catch (requestError) {
      const response = (requestError as { response?: { data?: { error?: string; message?: string } } }).response;
      const code = response?.data?.error;

      setLinkError(
        (code && LINK_ERROR_MESSAGES[code]) || response?.data?.message || "Nie udało się wygenerować linku.",
      );
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const copyLink = async () => {
    if (!link) {
      return;
    }

    try {
      await navigator.clipboard.writeText(link);
      void message.success("Link skopiowany do schowka");
    } catch {
      // Clipboard unavailable (permissions/insecure context) — the link stays
      // visible and selectable in the modal, so manual copy still works.
      void message.warning("Nie udało się skopiować automatycznie — zaznacz link ręcznie");
    }
  };

  const offerTag = () => {
    if (!offer) {
      return null;
    }

    if (offer.revokedAt) {
      return <Tag>Oferta cofnięta {dayjs(offer.revokedAt).format("DD.MM.YYYY")}</Tag>;
    }

    if (!isGrantActive) {
      return <Tag>Oferta wygasła {dayjs(offer.expiresAt).format("DD.MM.YYYY")}</Tag>;
    }

    return (
      <Tooltip
        title={
          <>
            {offer.emailSentAt ? `Mail wysłany ${dayjs(offer.emailSentAt).format("DD.MM.YYYY HH:mm")}` : "Bez maila"}
            <br />
            {followUpLabel(offer.followUp)}
          </>
        }
      >
        <Tag color="gold">
          Dopłata {rewardLabel(offer)} do {dayjs(offer.expiresAt).format("DD.MM.YYYY")}
        </Tag>
      </Tooltip>
    );
  };

  return (
    <>
      {/* ml-4 keeps the same gap after the installment counter as the sibling
          tags in ProductClientSubscriptionSettings. One dropdown instead of a
          row of links: with the tag, the counter and four actions the line was
          unreadable (TRE-1737). */}
      <Space size={4} className="ml-4">
        {offerTag()}
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              // The link only works where an offer exists — without one the
              // client has nothing to open.
              ...(canOffer && hasOffer
                ? [
                    {
                      key: "link",
                      icon: <LinkOutlined />,
                      label: "Generuj link dopłaty",
                      onClick: () => void generateLink(),
                    },
                  ]
                : []),
              ...(canOffer
                ? [
                    {
                      key: "offer",
                      icon: <GiftOutlined />,
                      label: isGrantActive ? "Zmień ofertę" : "Przyznaj ofertę dopłaty",
                      onClick: openOfferModal,
                    },
                  ]
                : []),
              // Available without an offer, and after the offer is gone: a
              // client can transfer the balance whenever she likes, and that
              // money still has to be booked (TRE-1737, TRE-2016).
              ...(canSettle
                ? [
                    {
                      key: "settle",
                      icon: <BankOutlined />,
                      label: "Spłacono całość (przelew)",
                      onClick: openSettleModal,
                    },
                  ]
                : []),
              ...(isGrantActive
                ? [
                    { type: "divider" as const, key: "divider" },
                    {
                      key: "revoke",
                      danger: true,
                      label: "Cofnij ofertę",
                      onClick: () => setShowRevokeConfirm(true),
                    },
                  ]
                : []),
            ],
          }}
        >
          {/* No gift icon on the trigger: the menu is no longer only about
              granting an offer — it also books a transfer. */}
          <Button type="link" loading={isGeneratingLink || saving}>
            Dopłata do całości <DownOutlined />
          </Button>
        </Dropdown>
      </Space>

      <Modal
        title="Cofnąć ofertę dopłaty?"
        open={showRevokeConfirm}
        okText="Cofnij ofertę"
        okButtonProps={{ danger: true, loading: saving }}
        onCancel={() => setShowRevokeConfirm(false)}
        onOk={() => void onRevoke()}
      >
        Baner w aplikacji zniknie, a wysłany link przestanie działać.
      </Modal>

      <PayoffOfflineSettlementModal
        productClient={productClient}
        open={showSettleModal}
        onClose={() => setShowSettleModal(false)}
        onSettled={refresh}
      />

      <Modal
        title="Oferta dopłaty do całości rat"
        open={showOfferModal}
        okText="Przyznaj"
        onCancel={() => setShowOfferModal(false)}
        onOk={() => formController.submit()}
        okButtonProps={{ loading: saving, disabled: saving }}
      >
        <Form<OfferFormModel> form={formController} layout="vertical" onFinish={onSubmit}>
          {formValues.rewardType === "discount" && (
            <Form.Item
              label="Rabat"
              name="discountPct"
              tooltip="Liczony od pozostałego salda rat po rabatach już aktywnych na subskrypcji."
              rules={[{ required: true, type: "number", min: 1, max: 90 }]}
            >
              <InputNumber min={1} max={90} precision={0} addonAfter="%" />
            </Form.Item>
          )}
          <Form.Item
            label="Oferta ważna do"
            name="expiresAt"
            tooltip="Data bezwzględna — niezależna od okna liczonego od zakupu na cenie."
            rules={[{ required: true, message: "Podaj datę końca oferty" }]}
          >
            <DatePicker format="DD.MM.YYYY" disabledDate={(current) => current && current.isBefore(dayjs(), "day")} />
          </Form.Item>
          <Form.Item label="Wyślij maila z ofertą" name="sendEmail" valuePropName="checked">
            <Switch />
          </Form.Item>
          <PayoffOfferCampaignFields values={formValues} />
        </Form>
      </Modal>

      <Modal
        title="Link dopłaty do całości"
        open={showLinkModal}
        onCancel={() => setShowLinkModal(false)}
        footer={
          link ? (
            <Button type="primary" onClick={() => void copyLink()}>
              Kopiuj link
            </Button>
          ) : null
        }
      >
        {isGeneratingLink && <Typography.Paragraph>Generowanie linku…</Typography.Paragraph>}
        {linkError && <Alert type="warning" showIcon message={linkError} />}
        {link && (
          <>
            <Typography.Paragraph copyable={{ text: link }} style={{ wordBreak: "break-all" }}>
              {link}
            </Typography.Paragraph>
            <Alert
              type="info"
              showIcon
              message="Kod w linku jest jednorazowy i ważny 30 minut. Po otwarciu przez klientkę link przestaje działać — w razie potrzeby wygeneruj nowy."
            />
          </>
        )}
      </Modal>
    </>
  );
};
